// Public, UNAUTHENTICATED site-induction / self-service form surface (QR / shareable link).
// Strictly scoped to a single link token — no app shell, no other data. Mobile-first.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, PenLine, ShieldCheck, AlertCircle, HardHat, LogIn, LogOut, Camera, MapPin, FileText, Plus, Trash2 } from "lucide-react";
import { saveDraft, getDraft, clearDraft } from "@/lib/offlineDraft";
import { useOnline } from "@/hooks/useOnline";
import { SyncStatusIndicator } from "@/components/SyncStatusIndicator";
import { personTypeLabel } from "@/lib/onsiteFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight formatting for talk briefing content: **bold** spans + "- " bullet
// lines grouped into lists. Kept deliberately simple (no full markdown/HTML) —
// matches the plain-textarea authoring convention used across the app.
function renderInline(text, keyPrefix) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
        part.startsWith("**") && part.endsWith("**")
            ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
            : <span key={`${keyPrefix}-${i}`}>{part}</span>
    ));
}
function renderBriefing(text) {
    if (!text) return null;
    const nodes = [];
    let bullets = [];
    const flush = () => {
        if (bullets.length) {
            nodes.push(<ul key={`ul-${nodes.length}`} className="list-disc pl-5 space-y-1">
                {bullets.map((l, i) => <li key={i}>{renderInline(l, `b-${nodes.length}-${i}`)}</li>)}
            </ul>);
            bullets = [];
        }
    };
    text.split("\n").forEach((raw, i) => {
        const line = raw.trim();
        if (!line) { flush(); return; }
        if (line.startsWith("- ") || line.startsWith("* ")) { bullets.push(line.slice(2)); return; }
        flush();
        nodes.push(<p key={`p-${i}`}>{renderInline(line, `p-${i}`)}</p>);
    });
    flush();
    return nodes;
}

function SignaturePad({ brand, onChange, testid, disabled }) {
    const ref = useRef(null);
    const drawing = useRef(false);
    const [has, setHas] = useState(false);
    function pos(e) {
        const c = ref.current, r = c.getBoundingClientRect();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: cx - r.left, y: cy - r.top };
    }
    function start(e) { if (disabled) return; e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); }
    function move(e) {
        if (disabled || !drawing.current) return; e.preventDefault();
        const { x, y } = pos(e); const ctx = ref.current.getContext("2d");
        ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#1F1F1F";
        ctx.lineTo(x, y); ctx.stroke(); setHas(true);
    }
    function end() { if (disabled || !drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); }
    function clear() { if (disabled) return; const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHas(false); onChange(null); }
    return (
        <div>
            <canvas ref={ref} width={480} height={150} data-testid={testid}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className={`w-full rounded-lg border bg-[#FCFBF9] touch-none ${disabled ? "opacity-50" : ""}`} style={{ borderColor: "#E5E0DA" }} />
            {disabled ? (
                <span className="text-[11px] text-[#9A968F] ml-1" data-testid="register-signature-locked">Tick the confirmation box above to sign</span>
            ) : (
                <>
                    <button type="button" onClick={clear} className="text-[11px] text-[#9A968F] hover:text-[#1F1F1F] mt-1">Clear signature</button>
                    {!has && <span className="text-[11px] text-[#9A968F] ml-3">Sign above with your finger or mouse</span>}
                </>
            )}
        </div>
    );
}

export default function PublicFormPage() {
    const { token } = useParams();
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [doneMsg, setDoneMsg] = useState(null);
    // attendance mode
    const [att, setAtt] = useState(null);        // null | "in" | "out"
    const [af, setAf] = useState({});            // attendance fields
    const [matches, setMatches] = useState(null); // sign-out ambiguity pick-list
    const [photos, setPhotos] = useState({});    // {field_id: File} — competency evidence
    const [certs, setCerts] = useState({});      // {field_id: [{id, label, file}]} — freeform training certs
    const [templateId, setTemplateId] = useState(null);   // induction template bound to this link
    const [sections, setSections] = useState(null);       // active sections
    const draftKey = `publicform:${token}`;
    const restoredRef = useRef(false);
    const online = useOnline();
    const [queued, setQueued] = useState(false); // last submit/attendance attempt failed offline, will auto-retry
    const pendingRetryRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/public/site-forms/${token}`)
            .then((r) => { setView(r.data); setTemplateId(r.data.default_template_id || r.data.template_id || null); setSections(r.data.sections || null); })
            .catch((e) => setError(e.response?.status === 404 ? "This link is invalid or has expired." : "Could not load this link."))
            .finally(() => setLoading(false));
    }, [token]);

    // Restore any in-progress answers/photos left over from a reload, crash,
    // or connectivity drop before this link was submitted.
    useEffect(() => {
        if (!view || restoredRef.current) return;
        restoredRef.current = true;
        (async () => {
            const local = await getDraft(draftKey);
            if (local && local.dirty && local.data) {
                if (local.data.answers) setAnswers(local.data.answers);
                if (local.data.af) setAf(local.data.af);
                if (local.data.photos) setPhotos(local.data.photos);
                if (local.data.certs) setCerts(local.data.certs);
            }
        })();
    }, [view, draftKey]);

    // Save-as-you-go — protects in-progress answers/photos against a lost
    // connection or an accidental reload before the final submit succeeds.
    useEffect(() => {
        if (!view || done) return;
        const handle = setTimeout(() => {
            saveDraft(draftKey, { answers, af, photos, certs }, true);
        }, 400);
        return () => clearTimeout(handle);
    }, [answers, af, photos, certs, view, done, draftKey]);

    useEffect(() => {
        if (done) clearDraft(draftKey);
    }, [done, draftKey]);

    // If a submit/attendance attempt failed purely due to no connectivity,
    // retry it automatically the moment we're back online.
    useEffect(() => {
        if (!online || !queued) return;
        const pending = pendingRetryRef.current;
        setQueued(false);
        pendingRetryRef.current = null;
        if (!pending) return;
        if (pending.type === "submit") submit();
        else if (pending.type === "attendance") attendance(pending.action, pending.attendanceId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [online, queued]);

    const brand = view?.primary_colour || "#C75A1B";
    const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
    const activeSections = sections || view?.sections || [];

    function nameFrom() {
        for (const s of activeSections)
            for (const f of s.fields || [])
                if (f.field_type === "text" && (f.label || "").toLowerCase() === "operative's name") return answers[f.id];
        return "";
    }

    async function submit() {
        if (view.mode === "register") {
            const name = (answers.__name || "").trim();
            if (!name) { setError("Please enter your name."); return; }
            setBusy(true); setError(null);
            try {
                const r = await axios.post(`${API}/public/site-forms/${token}/submit`,
                    { name, company: answers.__company || "", signature: answers.__signature || null });
                setDone(r.data?.name || name);
                setDoneMsg(r.data?.message || null);
            } catch (e) {
                if (!e.response) { setQueued(true); pendingRetryRef.current = { type: "submit" }; }
                else setError(e.response?.data?.detail || "Submission failed. Please try again.");
            }
            setBusy(false);
            return;
        }
        // client-side required-field validation
        for (const s of activeSections) {
            for (const f of s.fields || []) {
                if (f.field_type === "cert_upload_freeform") {
                    if (f.required && !(certs[f.id] || []).some((c) => c.file)) {
                        setError(`Please upload: ${f.label}`);
                        document.getElementById(`field-${f.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                        return;
                    }
                    continue;
                }
                if (f.required && !answers[f.id]) {
                    setError(`Please complete: ${f.label}`);
                    document.getElementById(`field-${f.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                    return;
                }
                if (f.wsr_role === "person_type_other") {
                    const ptField = (s.fields || []).find((x) => x.wsr_role === "person_type");
                    if (ptField && answers[ptField.id] === "Other" && !(answers[f.id] || "").trim()) {
                        setError(`Please complete: ${f.label}`);
                        document.getElementById(`field-${f.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                        return;
                    }
                }
            }
        }
        const name = (nameFrom() || "").trim();
        if (!name) { setError("Please enter your name."); return; }
        setBusy(true); setError(null);
        try {
            const r = await axios.post(`${API}/public/site-forms/${token}/submit`, { answers, name, template_id: templateId });
            const instanceId = r.data?.instance_id;
            // upload competency / evidence photos (best-effort, sequential)
            if (instanceId && Object.keys(photos).length) {
                for (const [fieldId, file] of Object.entries(photos)) {
                    if (!file) continue;
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("field_id", fieldId);
                    try { await axios.post(`${API}/public/site-forms/${token}/instances/${instanceId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* keep going */ }
                }
            }
            // upload freeform training certificates (best-effort, sequential)
            if (instanceId) {
                for (const [fieldId, rows] of Object.entries(certs)) {
                    for (const row of rows) {
                        if (!row.file) continue;
                        const fd = new FormData();
                        fd.append("file", row.file);
                        fd.append("field_id", fieldId);
                        fd.append("label", row.label || row.file.name);
                        try { await axios.post(`${API}/public/site-forms/${token}/instances/${instanceId}/certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* keep going */ }
                    }
                }
            }
            setDone(r.data?.name || name);
            setDoneMsg(r.data?.message || null);
        } catch (e) {
            if (!e.response) { setQueued(true); pendingRetryRef.current = { type: "submit" }; }
            else setError(e.response?.data?.detail || "Submission failed. Please try again.");
        }
        setBusy(false);
    }

    const setPhoto = (id, file) => setPhotos((p) => ({ ...p, [id]: file }));
    const setCertRows = (fieldId, rows) => setCerts((c) => ({ ...c, [fieldId]: rows }));

    const setAF = (k, v) => setAf((a) => ({ ...a, [k]: v }));

    async function attendance(action, attendanceId) {
        setError(null);
        if (action === "in") {
            if (!(af.name || "").trim()) { setError("Please enter your name."); return; }
            if (!(af.mobile || "").trim()) { setError("Please enter your mobile number."); return; }
            if (!(af.email || "").trim()) { setError("Please enter your email — it's how you'll get your one-tap sign-out link."); return; }
            if (!af.person_type) { setError("Please tell us who is signing in."); return; }
            const companyVal = af.company === "__other" ? (af.company_other || "").trim() : (af.company || "");
            if (af.person_type === "Subcontractor" && !companyVal) { setError("Please enter your company."); return; }
            if (af.person_type === "Other" && !(af.person_type_other || "").trim()) { setError("Please tell us who this is (e.g. Saltash staff, Trainee, Apprentice)."); return; }
        }
        if (action === "out" && !attendanceId && !(af.mobile || "").trim() && !(af.name || "").trim()) {
            setError("Please enter your mobile number to sign out."); return;
        }
        setBusy(true);
        try {
            const company = af.company === "__other" ? (af.company_other || "").trim() : (af.company || "");
            const r = await axios.post(`${API}/public/site-forms/${token}/attendance`, {
                action, name: (af.name || "").trim(), email: (af.email || "").trim(),
                mobile: (af.mobile || "").trim(), company, person_type: af.person_type || null,
                person_type_other: af.person_type === "Other" ? (af.person_type_other || "").trim() : null,
                signature: af.signature || null,
                attendance_id: attendanceId || null,
            });
            if (r.data?.ambiguous) { setMatches(r.data.matches || []); setBusy(false); return; }
            setDone(r.data?.name || af.name || "");
            setDoneMsg(r.data?.message || null);
        } catch (e) {
            if (!e.response) { setQueued(true); pendingRetryRef.current = { type: "attendance", action, attendanceId }; }
            else setError(e.response?.data?.detail || "Something went wrong. Please try again.");
        }
        setBusy(false);
    }

    async function lookupPersonType() {
        const email = (af.email || "").trim();
        const mobile = (af.mobile || "").trim();
        if (!email && !mobile) return;
        try {
            const p = new URLSearchParams();
            if (email) p.set("email", email);
            if (mobile) p.set("mobile", mobile);
            const r = await axios.get(`${API}/public/site-forms/${token}/attendance/lookup?${p.toString()}`);
            if (r.data?.person_type && !af.person_type) setAF("person_type", r.data.person_type);
            if (r.data?.person_type_other && !af.person_type_other) setAF("person_type_other", r.data.person_type_other);
        } catch { /* best-effort prefill, ignore failures */ }
    }
    if (loading) return <Center><Loader2 className="animate-spin" size={28} /></Center>;
    if (error && !view) return <Center><div className="text-center"><AlertCircle className="mx-auto mb-2 text-red-500" size={28} /><p className="text-[#6F6A64]">{error}</p></div></Center>;
    if (view && (view.active === false || view.accepting === false))
        return <Center><div className="text-center max-w-sm" data-testid="public-form-closed"><ShieldCheck className="mx-auto mb-2 text-[#9A968F]" size={30} /><p className="text-[#6F6A64]">This sign-on link is no longer active. Please see the site manager.</p></div></Center>;

    if (done) return (
        <Center>
            <div className="text-center max-w-sm" data-testid="public-form-done">
                <CheckCircle2 className="mx-auto mb-3" size={46} style={{ color: brand }} />
                <h1 className="text-xl font-bold text-[#1F1F1F]">Thank you, {String(done).split(" ")[0]}</h1>
                <p className="text-[#6F6A64] mt-1.5 text-[14px]">{doneMsg || <>Your induction for <b>{view.site_name || view.label}</b> has been recorded. Please see the site manager to be authorised onto site.</>}</p>
            </div>
        </Center>
    );

    if (view?.mode === "attendance") return (
        <AttendanceScreen view={view} brand={brand} att={att} setAtt={setAtt} af={af} setAF={setAF}
            busy={busy} queued={queued} error={error} matches={matches} setMatches={setMatches}
            onSubmit={attendance} onLookup={lookupPersonType} />
    );

    return (
        <div className="min-h-screen bg-[#F5F3F0] py-6 px-4" data-testid="public-form-page">
            <div className="max-w-xl mx-auto">
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ background: brand }}><HardHat size={18} /></div>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F]">{view.client_name || "Site sign-on"}</div>
                        <div className="text-[11px] text-[#9A968F]">{[view.site_name, view.project_name].filter(Boolean).join(" · ")}</div>
                    </div>
                </div>

                <div className="rounded-[16px] border shadow-sm overflow-hidden bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-4" style={{ background: brand }}>
                        <h1 className="text-lg font-bold text-white">{view.label || view.template_name}</h1>
                        <p className="text-[12.5px] text-white/85 mt-0.5">
                            {view.mode === "register"
                                ? "Please read the briefing below, then sign to confirm you understood it."
                                : "Please complete this induction on your own device before starting work."}
                        </p>
                    </div>

                    <div className="p-5 space-y-6">
                        {view.mode === "register" ? (
                            <div className="space-y-4" data-testid="public-register-form">
                                {(view.briefing_body || view.briefing_url) && (
                                    <div data-testid="public-briefing" className="rounded-xl border p-4 space-y-3" style={{ borderColor: brand, background: "#FCFBF9" }}>
                                        <h2 className="text-[13px] font-bold uppercase tracking-wider" style={{ color: brand }}>Today's talk</h2>
                                        {view.briefing_body && (
                                            <div className="text-[13px] text-[#1F1F1F] leading-relaxed space-y-2" data-testid="public-briefing-body">{renderBriefing(view.briefing_body)}</div>
                                        )}
                                        {view.briefing_url && (
                                            <a data-testid="public-briefing-doc-link" href={`${API}/public/site-forms/${view.briefing_url}`} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold underline" style={{ color: brand }}>
                                                View briefing document
                                            </a>
                                        )}
                                    </div>
                                )}
                                <label className="block">
                                    <span className="text-[13px] font-semibold text-[#1F1F1F]">Full name <span className="text-red-500">*</span></span>
                                    <input data-testid="register-name" value={answers.__name || ""} onChange={(e) => set("__name", e.target.value)}
                                        className="w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] mt-1.5 focus:outline-none" />
                                </label>
                                <label className="block">
                                    <span className="text-[13px] font-semibold text-[#1F1F1F]">Company / trade</span>
                                    <input data-testid="register-company" value={answers.__company || ""} onChange={(e) => set("__company", e.target.value)}
                                        className="w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] mt-1.5 focus:outline-none" />
                                </label>
                                {(view.briefing_body || view.briefing_url) && (
                                    <label className="flex items-start gap-2 text-[13px] font-semibold text-[#1F1F1F]" data-testid="public-briefing-confirm">
                                        <input type="checkbox" checked={!!answers.__confirmed} onChange={(e) => set("__confirmed", e.target.checked)} className="mt-0.5 w-4 h-4" />
                                        I have read and understood this talk
                                    </label>
                                )}
                                <div>
                                    <span className="text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1"><PenLine size={13} /> Signature</span>
                                    <div className="mt-1.5">
                                        <SignaturePad brand={brand} onChange={(v) => set("__signature", v)} testid="register-signature"
                                            disabled={!!(view.briefing_body || view.briefing_url) && !answers.__confirmed} />
                                    </div>
                                </div>
                                {error && <div className="text-[12.5px] text-red-600 flex items-center gap-1"><AlertCircle size={13} /> {error}</div>}
                                {queued && <SyncStatusIndicator status="offline" testId="public-form-sync-status" />}
                                <button data-testid="public-form-submit" onClick={submit}
                                    disabled={busy || (!!(view.briefing_body || view.briefing_url) && !answers.__confirmed)}
                                    className="w-full h-12 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: brand }}>
                                    {busy ? <Loader2 className="animate-spin" size={17} /> : <CheckCircle2 size={17} />} Sign in
                                </button>
                            </div>
                        ) : (<>
                        {(view.site_blocks || []).length > 0 && (
                            <div data-testid="public-site-blocks" className="rounded-xl border p-4 space-y-4" style={{ borderColor: brand, background: "#FCFBF9" }}>
                                <h2 className="text-[13px] font-bold uppercase tracking-wider" style={{ color: brand }}>Site-specific rules — please read</h2>
                                {view.site_blocks.map((b) => (
                                    <div key={b.id} data-testid={`site-block-${b.id}`}>
                                        <div className="text-[13.5px] font-semibold text-[#1F1F1F]">{b.heading}</div>
                                        {b.body && <p className="text-[12.5px] text-[#6F6A64] whitespace-pre-line leading-relaxed mt-1">{b.body}</p>}
                                        {b.image_url && (
                                            <img data-testid={`site-block-img-${b.id}`} alt={b.heading}
                                                src={`${API}/public/site-forms/${b.image_url}`}
                                                onError={(e) => { e.target.style.display = "none"; }}
                                                className="mt-2 rounded-lg border max-h-64 w-full object-cover" style={{ borderColor: "#E5E0DA" }} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {(view.assembly_points || []).length > 0 && (
                            <div data-testid="public-assembly-points" className="rounded-xl border p-4 space-y-4" style={{ borderColor: brand, background: "#FCFBF9" }}>
                                <h2 className="text-[13px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: brand }}><MapPin size={13} /> Assembly points — where to gather</h2>
                                {view.assembly_points.map((b) => (
                                    <div key={b.id} data-testid={`assembly-point-${b.id}`}>
                                        <div className="text-[13.5px] font-semibold text-[#1F1F1F]">{b.heading}</div>
                                        {b.body && <p className="text-[12.5px] text-[#6F6A64] whitespace-pre-line leading-relaxed mt-1">{b.body}</p>}
                                        {b.image_url && (
                                            <img data-testid={`assembly-point-img-${b.id}`} alt={b.heading}
                                                src={`${API}/public/site-forms/${b.image_url}`}
                                                onError={(e) => { e.target.style.display = "none"; }}
                                                className="mt-2 rounded-lg border max-h-64 w-full object-cover" style={{ borderColor: "#E5E0DA" }} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {activeSections.map((s) => (
                            <div key={s.id} data-testid={`public-section-${s.key || s.id}`}>
                                <h2 className="text-[13px] font-bold uppercase tracking-wider mb-3 pb-1.5 border-b" style={{ color: brand, borderColor: "#F0EDE8" }}>{s.title}</h2>
                                <div className="space-y-4">
                                    {(s.fields || []).map((f) => {
                                        if (f.wsr_role === "person_type_other") {
                                            const ptField = (s.fields || []).find((x) => x.wsr_role === "person_type");
                                            if (!ptField || answers[ptField.id] !== "Other") return null;
                                        }
                                        return (
                                        <div key={f.id} id={`field-${f.id}`}>
                                            {f.field_type === "instruction" ? (
                                                <div className="text-[12.5px] text-[#6F6A64] leading-relaxed space-y-2">{renderBriefing(f.content || f.label)}</div>
                                            ) : f.field_type === "cert_upload_freeform" ? (
                                                <div>
                                                    <span className="text-[13px] font-semibold text-[#1F1F1F]">{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                                                    <CertUploadField brand={brand} rows={certs[f.id] || []} setRows={(rows) => setCertRows(f.id, rows)} />
                                                </div>
                                            ) : (
                                                <label className="block">
                                                    <span className="text-[13px] font-semibold text-[#1F1F1F]">{f.label}{f.required && <span className="text-red-500"> *</span>}{f.evidence_required && <span className="text-[11px] font-normal text-[#9A968F]"> · evidence</span>}</span>
                                                    <Field f={f} value={answers[f.id]} set={(v) => set(f.id, v)} brand={brand} photo={photos[f.id]} setPhoto={(file) => setPhoto(f.id, file)} />
                                                </label>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        {error && <div className="text-[12.5px] text-red-600 flex items-center gap-1"><AlertCircle size={13} /> {error}</div>}
                        {queued && <SyncStatusIndicator status="offline" testId="public-form-sync-status" />}
                        <button data-testid="public-form-submit" onClick={submit} disabled={busy}
                            className="w-full h-12 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: brand }}>
                            {busy ? <Loader2 className="animate-spin" size={17} /> : <CheckCircle2 size={17} />} Submit induction
                        </button>
                        </>)}
                    </div>
                </div>
                <p className="text-center text-[11px] text-[#9A968F] mt-4">Secured submission · Powered by Priority Risk</p>
            </div>
        </div>
    );
}

function Field({ f, value, set, brand, photo, setPhoto }) {
    const cls = "w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] focus:outline-none mt-1.5";
    if (f.field_type === "photo") {
        return (
            <div className="mt-1.5">
                <label data-testid={`field-photo-${f.id}`}
                    className="flex items-center gap-2 h-11 px-3 rounded-lg border border-dashed cursor-pointer text-[13px]"
                    style={{ borderColor: photo ? brand : "#E5E0DA", color: photo ? brand : "#9A968F", background: "#FCFBF9" }}>
                    <Camera size={16} />
                    <span className="truncate flex-1">{photo ? photo.name : "Take / upload a photo"}</span>
                    {photo && <span className="text-[11px] text-green-600 font-semibold">Attached</span>}
                    <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => setPhoto && setPhoto(e.target.files?.[0] || null)} />
                </label>
            </div>
        );
    }
    if (f.field_type === "yes_no") {
        return (
            <div className="flex gap-2 mt-1.5" data-testid={`field-yesno-${f.id}`}>
                {["Yes", "No"].map((opt) => (
                    <button key={opt} type="button" onClick={() => set(opt)}
                        className="flex-1 h-11 rounded-lg border text-[14px] font-semibold transition-colors"
                        style={value === opt ? { background: brand, color: "#fff", borderColor: brand } : { borderColor: "#E5E0DA", color: "#1F1F1F", background: "#fff" }}>
                        {opt}
                    </button>
                ))}
            </div>
        );
    }
    if (f.field_type === "textarea")
        return <textarea value={value || ""} onChange={(e) => set(e.target.value)} rows={3} className={cls + " h-auto py-2"} />;
    if (f.field_type === "dropdown" || f.field_type === "multi_select")
        return (
            <select value={value || ""} onChange={(e) => set(e.target.value)} className={cls}>
                <option value="">— Select —</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    if (f.field_type === "signature")
        return <div className="mt-1.5"><SignaturePad brand={brand} onChange={set} testid={`public-signature-${f.id}`} /></div>;
    if (["number", "date"].includes(f.field_type))
        return <input type={f.field_type} value={value || ""} onChange={(e) => set(e.target.value)} className={cls} />;
    return <input type="text" value={value || ""} onChange={(e) => set(e.target.value)} placeholder={f.placeholder || ""} className={cls} />;
}

function CertUploadField({ brand, rows, setRows }) {
    const add = () => setRows([...rows, { id: `c${Date.now()}${Math.floor(Math.random() * 999)}`, label: "", file: null }]);
    const update = (id, patch) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const remove = (id) => setRows(rows.filter((r) => r.id !== id));
    return (
        <div className="mt-1.5 space-y-2" data-testid="cert-upload-field">
            {rows.map((r, i) => (
                <div key={r.id} data-testid={`cert-upload-row-${i}`} className="flex items-center gap-2 border rounded-lg p-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                    <input data-testid={`cert-upload-label-${i}`} value={r.label} onChange={(e) => update(r.id, { label: e.target.value })}
                        placeholder="e.g. CSCS card, First Aid at Work…" className="flex-1 h-9 px-2.5 rounded-md border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }} />
                    <label data-testid={`cert-upload-file-${i}`} className="h-9 px-2.5 rounded-md border text-[12px] font-semibold inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                        style={{ borderColor: r.file ? brand : "#E5E0DA", color: r.file ? brand : "#9A968F", background: "#fff" }}>
                        <FileText size={13} /> {r.file ? r.file.name.slice(0, 14) : "Choose file"}
                        <input type="file" className="hidden" onChange={(e) => update(r.id, { file: e.target.files?.[0] || null })} />
                    </label>
                    <button type="button" data-testid={`cert-upload-remove-${i}`} onClick={() => remove(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
            ))}
            <button type="button" data-testid="cert-upload-add" onClick={add}
                className="text-[12px] font-semibold rounded-full px-3 py-1.5 border inline-flex items-center gap-1.5" style={{ borderColor: brand, color: brand }}>
                <Plus size={13} /> Add a certificate
            </button>
        </div>
    );
}

function Center({ children }) {
    return <div className="min-h-screen grid place-items-center bg-[#F5F3F0] p-4">{children}</div>;
}

function AttendanceScreen({ view, brand, att, setAtt, af, setAF, busy, queued, error, matches, setMatches, onSubmit, onLookup }) {
    const inputCls = "w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] mt-1.5 focus:outline-none";
    const companies = view.companies || [];
    const personTypes = view.person_types || [];
    return (
        <div className="min-h-screen bg-[#F5F3F0] py-6 px-4" data-testid="public-attendance-page">
            <div className="max-w-xl mx-auto">
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ background: brand }}><HardHat size={18} /></div>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F]">{view.client_name || "Site sign-in"}</div>
                        <div className="text-[11px] text-[#9A968F]">{[view.site_name, view.project_name].filter(Boolean).join(" · ")}</div>
                    </div>
                </div>

                <div className="rounded-[16px] border shadow-sm overflow-hidden bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-4" style={{ background: brand }}>
                        <h1 className="text-lg font-bold text-white">{view.label || "Site sign-in / sign-out"}</h1>
                        <p className="text-[12.5px] text-white/85 mt-0.5">Everyone arriving on site must sign in, and sign out when leaving.</p>
                    </div>

                    <div className="p-5 space-y-5">
                        {!att && (
                            <div className="grid grid-cols-2 gap-3" data-testid="attendance-choice">
                                <button data-testid="attendance-choose-in" onClick={() => setAtt("in")}
                                    className="h-24 rounded-xl text-white font-bold text-[16px] flex flex-col items-center justify-center gap-1" style={{ background: brand }}>
                                    <LogIn size={22} /> Sign in
                                </button>
                                <button data-testid="attendance-choose-out" onClick={() => setAtt("out")}
                                    className="h-24 rounded-xl border-2 font-bold text-[16px] flex flex-col items-center justify-center gap-1" style={{ borderColor: brand, color: brand }}>
                                    <LogOut size={22} /> Sign out
                                </button>
                            </div>
                        )}

                        {att === "in" && (
                            <div className="space-y-4" data-testid="attendance-in-form">
                                <Labeled label="Full name" required>
                                    <input data-testid="att-name" value={af.name || ""} onChange={(e) => setAF("name", e.target.value)} className={inputCls} />
                                </Labeled>
                                <Labeled label="Mobile number" required>
                                    <input data-testid="att-mobile" type="tel" value={af.mobile || ""} onChange={(e) => setAF("mobile", e.target.value)} onBlur={onLookup} className={inputCls} />
                                </Labeled>
                                <Labeled label="Email" required>
                                    <input data-testid="att-email" type="email" required value={af.email || ""} onChange={(e) => setAF("email", e.target.value)} onBlur={onLookup} className={inputCls} />
                                </Labeled>
                                <p className="text-[11.5px] text-[#9A968F] -mt-2">You'll get a one-tap sign-out link sent to this email — no need to walk back to this poster to sign out.</p>
                                <Labeled label="Who is signing in?" required>
                                    <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="att-person-type">
                                        {personTypes.map((pt) => (
                                            <button key={pt} type="button" data-testid={`att-person-type-${pt.toLowerCase().replace(/\s+/g, "-")}`}
                                                onClick={() => setAF("person_type", pt)}
                                                className="h-10 rounded-lg border text-[12.5px] font-semibold transition-colors"
                                                style={af.person_type === pt ? { background: brand, color: "#fff", borderColor: brand } : { borderColor: "#E5E0DA", color: "#1F1F1F", background: "#fff" }}>
                                                {personTypeLabel(pt)}
                                            </button>
                                        ))}
                                    </div>
                                </Labeled>
                                {af.person_type === "Other" && (
                                    <Labeled label="Please specify" required>
                                        <input data-testid="att-person-type-other" placeholder="e.g. Saltash staff, Trainee, Apprentice"
                                            value={af.person_type_other || ""} onChange={(e) => setAF("person_type_other", e.target.value)} className={inputCls} />
                                    </Labeled>
                                )}
                                <Labeled label="Company" required={af.person_type === "Subcontractor"}>
                                    <select data-testid="att-company" value={af.company || ""} onChange={(e) => setAF("company", e.target.value)} className={inputCls}>
                                        <option value="">— Select your company —</option>
                                        {companies.map((c) => <option key={c} value={c}>{c}</option>)}
                                        <option value="__other">Add new / not listed…</option>
                                    </select>
                                    {af.company === "__other" && (
                                        <input data-testid="att-company-other" placeholder="Enter company name" value={af.company_other || ""}
                                            onChange={(e) => setAF("company_other", e.target.value)} className={inputCls} />
                                    )}
                                </Labeled>
                                <div>
                                    <span className="text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1"><PenLine size={13} /> Signature <span className="text-[#9A968F] font-normal">(optional)</span></span>
                                    <div className="mt-1.5"><SignaturePad brand={brand} onChange={(v) => setAF("signature", v)} testid="att-signature" /></div>
                                </div>
                                {error && <ErrorLine msg={error} />}
                                {queued && <SyncStatusIndicator status="offline" testId="attendance-sync-status-in" />}
                                <SubmitBtn testid="attendance-submit-in" brand={brand} busy={busy} icon={LogIn} label="Sign in" onClick={() => onSubmit("in")} />
                                <BackBtn onClick={() => setAtt(null)} />
                            </div>
                        )}

                        {att === "out" && (
                            <div className="space-y-4" data-testid="attendance-out-form">
                                {matches ? (
                                    <div className="space-y-2" data-testid="attendance-pick-list">
                                        <p className="text-[13px] text-[#6F6A64]">More than one match — please tap your sign-in:</p>
                                        {matches.map((m) => (
                                            <button key={m.id} data-testid={`attendance-pick-${m.id}`} onClick={() => onSubmit("out", m.id)}
                                                className="w-full text-left border rounded-lg px-3 py-2.5 hover:bg-[#FCFBF9]" style={{ borderColor: "#E5E0DA" }}>
                                                <div className="text-[13.5px] font-semibold text-[#1F1F1F]">{m.name}</div>
                                                <div className="text-[11px] text-[#9A968F]">{[m.company, fmtTime(m.time_in)].filter(Boolean).join(" · in at ")}</div>
                                            </button>
                                        ))}
                                        <BackBtn onClick={() => { setMatches(null); }} />
                                    </div>
                                ) : (<>
                                    <p className="text-[13px] text-[#6F6A64]">Enter the mobile number you signed in with.</p>
                                    <Labeled label="Mobile number" required>
                                        <input data-testid="att-out-mobile" type="tel" value={af.mobile || ""} onChange={(e) => setAF("mobile", e.target.value)} className={inputCls} />
                                    </Labeled>
                                    <Labeled label="Name (if you didn't give a mobile)">
                                        <input data-testid="att-out-name" value={af.name || ""} onChange={(e) => setAF("name", e.target.value)} className={inputCls} />
                                    </Labeled>
                                    {error && <ErrorLine msg={error} />}
                                    {queued && <SyncStatusIndicator status="offline" testId="attendance-sync-status-out" />}
                                    <SubmitBtn testid="attendance-submit-out" brand={brand} busy={busy} icon={LogOut} label="Sign out" onClick={() => onSubmit("out")} />
                                    <BackBtn onClick={() => setAtt(null)} />
                                </>)}
                            </div>
                        )}
                    </div>
                </div>
                <p className="text-center text-[11px] text-[#9A968F] mt-4">Secured submission · Powered by Priority Risk</p>
            </div>
        </div>
    );
}

function Labeled({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[13px] font-semibold text-[#1F1F1F]">{label}{required && <span className="text-red-500"> *</span>}</span>
            {children}
        </label>
    );
}
function ErrorLine({ msg }) {
    return <div className="text-[12.5px] text-red-600 flex items-center gap-1"><AlertCircle size={13} /> {msg}</div>;
}
function SubmitBtn({ testid, brand, busy, icon: Icon, label, onClick }) {
    return (
        <button data-testid={testid} onClick={onClick} disabled={busy}
            className="w-full h-12 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: brand }}>
            {busy ? <Loader2 className="animate-spin" size={17} /> : <Icon size={17} />} {label}
        </button>
    );
}
function BackBtn({ onClick }) {
    return <button data-testid="attendance-back" onClick={onClick} className="w-full text-[12.5px] text-[#9A968F] hover:text-[#1F1F1F]">← Back</button>;
}
function fmtTime(s) {
    if (!s) return "";
    try { return new Date(s).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}
